import React from "react";

import ProgramImpact from "../../components/program/programImpact";
import TravelFlexibility from "../../components/program/travelFlexibility";
import CollapsibleWrapper from "../../components/shared/collapsibleWrapper";
import DownloadBanner from "../../components/shared/downloadBanner";
import EnquireCard from "../../components/shared/enquireCard";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import MountainIllustration from "../../images/illustrations/Mountain_illustration.inline.svg";
import DarkIllustration from "../../images/program/program-tabs-ill-dark.inline.svg";
import brochureImage from "../../images/program/under18-brochure.png";
import { DesktopTabletOnly } from "../base/wrapper/device";
import GroupProgramTabs from "../group-program/programTabs";
import ProgramTabsAccordion from "./programTabsAccordion";

const ProgramInfo = ({
  programDetails,
  excerpt,
  programLocation,
  sharedDetails,
  testimonials,
  program,
  experiences,
  expTabTitle,
  keyMessageIcons,
  programHub,
  programPopular,
  programCapacity,
  programImpact,
  setAlumniOpenModal,
  travelDetails = {},
  location,
  groupInstitute = "",
  setItineraryOpenModal,
  itineraryOpenModal,
  setActiveTabSlidebySlug,
  getActiveTabSlidebySlug,
  enquireCardExtraDetails = {}
}) => {
  // programDetails.programTypes
  const programType =
    programDetails.programTypes && programDetails.programTypes.length
      ? programDetails.programTypes.map((pgmType) => pgmType.slug.toLowerCase())
      : [];

  const isGroupProgram = program?.uri.includes("group-travel-single") ?? false;
  const breakpoint = useBreakpoint();

  // setServiceCards(sCards);

  const programId = program?.databaseId ?? "";
  const programName = program?.title ?? "";
  const groupsRecordTypeId = "0123k000001QapgAAC";
  const programCode = program?.programCode ?? programId;

  const programIdentifier = programCode ?? programId;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  let applyNow = programId
    ? "/application-form/?" + programSuffix
    : "/application-form/";
  let enquireNow = programId
    ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}`
    : "/enquiry-form/";

  if (isGroupProgram) {
    applyNow = `/group-application-form/?type=application&program=${programCode}&program_title=${programName}&group_institute=${groupInstitute}`;
    enquireNow = `/enquiry-form/?type=group_enquiry&program=${programCode}&program_title=${programName}&record_type_id=${groupsRecordTypeId}`;
  }

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails() ?? {};

  let enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: stickyImage,
    cardDesc: isGroupProgram
      ? ""
      : `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
        "",
    cardBookDesc: null
  };

  if (!isGroupProgram) {
    enquireCardDetails = enquireCardExtraDetails;
  }

  const genericConfig =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const downloadBannerTitle =
    genericConfig?.teenBrochureTitle ?? "Under 18s brochure";
  const downloadBannerDescription =
    genericConfig?.teenBrochureDescription ??
    "We've got amazing programs tailored for young adults.";
  const downloadBannerLink = genericConfig?.teenBrochureDownloadLink ?? "";
  const applyCTALabel = isGroupProgram ? "Student <br/> Application" : "Apply";
  const enquireCTALabel = isGroupProgram ? "Educator <br/> Enquiry" : "Enquire";

  return (
    <>
      <section className="w-full bg-primary-bg mx-auto flex items-center justify-center relative sm:overflow-hidden">
        <DesktopTabletOnly>
          <DarkIllustration className="!absolute z-10 sm:z-0 w-52 sm:w-auto -bottom-108 sm:bottom-0 sm:top-1/3 -right-11 sm:right-0 hidden dark:md:block stroke-black-lighter" />
          <MountainIllustration className="absolute w-[150%] md:w-7/12 -bottom-[35vw] md:bottom-0 md:top-1/3 md:-right-[21vw] dark:hidden xl:w-2/5" />
        </DesktopTabletOnly>
        <section className="summary-section justify-center w-11/12 pt-12 sm:py-12 flex gap-10 flex-wrap sm:flex-nowrap sm:w-10/12">
          <div className="w-full md:sticky-left-section mr-auto md:!pr-4">
            <section>
              <div className="py-8 sm:pb-0 px-2">
                <h3 className="lato-300-latin pb-5 not-italic font-normal dark:!text-gray-generic">
                  Program information
                </h3>
                <CollapsibleWrapper
                  desc={excerpt}
                  descStyle="text-black-lighter dark:text-gray-generic text-sm md:text-base"
                  btnStyle="underline underline-offset-2 pt-2 text-gray-generic dark:text-white text-sm flex items-center gap-2"
                  wrapperContainer=""
                  visibleLength={breakpoint === "xs" ? 350 : 70}
                  readMoreAdditionalStyle={"md:line-clamp-none"}
                  type={"mobile-only"}
                  displayFullDesc={breakpoint !== "xs"}
                />
              </div>
              <div className="block sm:hidden">
                {programImpact?.list && programImpact?.list.length > 0 && (
                  <ProgramImpact programImpact={programImpact} />
                )}
              </div>
              {programType.includes("under-18") && downloadBannerLink && (
                <DownloadBanner
                  featuredImage={brochureImage}
                  displayTitle={downloadBannerTitle}
                  displayDescription={downloadBannerDescription}
                  ctaLink={downloadBannerLink}
                  ctaLabel="Download"
                  target={"_gviblank"}
                />
              )}
            </section>
            <EnquireCard
              isMobile={true}
              enquireNow={enquireNow}
              applyNow={applyNow}
              enquireCardDetails={enquireCardDetails}
              applyCTALabel={applyCTALabel}
              enquireCTALabel={enquireCTALabel}
            />

            {!isGroupProgram && travelDetails?.title?.length > 0 && (
              <div className="block sm:hidden pt-12">
                <TravelFlexibility
                  travelDetails={travelDetails}
                  isGVI={true}
                  hasBullets={false}
                  outerContainerClass=""
                  innerContainerClass=""
                  headingClass="dark:text-white"
                  listItemClass="text-xs"
                />
              </div>
            )}

            <section className="w-full col-span-full flex justify-center">
              {isGroupProgram ? (
                <>
                  <GroupProgramTabs
                    programDetails={programDetails}
                    programLocation={programLocation}
                    sharedDetails={sharedDetails}
                    program={program}
                    programHub={programHub}
                    setAlumniOpenModal={setAlumniOpenModal}
                    location={location}
                    testimonials={testimonials}
                    setItineraryOpenModal={setItineraryOpenModal}
                    setActiveTabSlidebySlug={setActiveTabSlidebySlug}
                    getActiveTabSlidebySlug={getActiveTabSlidebySlug}
                  />
                </>
              ) : (
                <ProgramTabsAccordion
                  programDetails={programDetails}
                  programLocation={programLocation}
                  sharedDetails={sharedDetails}
                  testimonials={testimonials}
                  program={program}
                  programHub={programHub}
                  programPopular={programPopular}
                  programCapacity={programCapacity}
                  experiences={experiences}
                  expTabTitle={expTabTitle}
                  setAlumniOpenModal={setAlumniOpenModal}
                  location={location}
                  setItineraryOpenModal={setItineraryOpenModal}
                  setActiveTabSlidebySlug={setActiveTabSlidebySlug}
                  getActiveTabSlidebySlug={getActiveTabSlidebySlug}
                />
              )}
            </section>
          </div>
        </section>
      </section>
    </>
  );
};

export default ProgramInfo;
